import React, { useState } from "react";
import styles from "./AuthorityBadge.module.css";

export type CitationMetadata = {
  effective_date?: string;
  is_current?: boolean;
  authority_level?: string;
  authority_label?: string;
  authority_description?: string;
  authority_priority?: number;
  regulation_title?: string;
  // Supersession details (populated when document has been superseded)
  superseded_by_title?: string;
  superseded_by_date?: string;
  superseded_by_id?: string;
};

type AuthorityBadgeProps = {
  metadata?: CitationMetadata;
};

/**
 * Displays authority level badge with tooltip for UK CAA regulatory citations.
 * Shows binding status, effective date, and supersession info to help users understand legal weight.
 */
export const AuthorityBadge: React.FC<AuthorityBadgeProps> = ({ metadata }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!metadata) return null;

  const {
    authority_label,
    authority_description,
    authority_priority = 5,
    is_current = true,
    effective_date,
    superseded_by_title,
    superseded_by_date,
  } = metadata;

  // Determine badge style based on priority
  const getBadgeClass = () => {
    if (!is_current) return styles.superseded;
    switch (authority_priority) {
      case 1:
        return styles.binding; // Assimilated Law - green
      case 2:
        return styles.delegated; // Statutory Instrument - teal
      case 3:
        return styles.safeHarbor; // CAP (CAA Publication) - blue
      case 4:
        return styles.guidance; // Guidance - gray
      default:
        return styles.unknown;
    }
  };

  // Format effective date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={styles.badgeContainer}>
      {/* Authority Badge */}
      <span
        className={`${styles.badge} ${getBadgeClass()}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        tabIndex={0}
        role="note"
        aria-describedby="authority-tooltip"
      >
        {authority_label || "Unknown"}
      </span>

      {/* Superseded Warning with Replacement Info */}
      {!is_current && (
        <span className={styles.supersededWarning} title="This content has been replaced">
          Superseded
        </span>
      )}

      {/* Effective Date */}
      {effective_date && is_current && (
        <span className={styles.effectiveDate}>
          In force: {formatDate(effective_date)}
        </span>
      )}

      {/* Supersession Details - show replacement document */}
      {!is_current && superseded_by_title && (
        <span className={styles.supersededDetail}>
          Replaced by {superseded_by_title}
          {superseded_by_date && ` (${formatDate(superseded_by_date)})`}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && authority_description && (
        <div
          id="authority-tooltip"
          className={styles.tooltip}
          role="tooltip"
        >
          <div className={styles.tooltipContent}>
            <strong>{authority_label}</strong>
            <p>{authority_description}</p>
            {!is_current && (
              <p className={styles.tooltipWarning}>
                This regulation has been superseded.
                {superseded_by_title && (
                  <> Replaced by <strong>{superseded_by_title}</strong>
                  {superseded_by_date && ` on ${formatDate(superseded_by_date)}`}.</>
                )}
                {" "}The information may not reflect current UK law.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
